import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatCOP, formatDate } from "../../lib/apiClient";
import { loadN2Offer } from "../../lib/useN2Offer";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function N2OfferSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadN2Offer(true).then(setForm); }, []);
  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDel = (i, k, v) => set("deliverables", form.deliverables.map((d, j) => (j === i ? { ...d, [k]: v } : d)));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        price_reviewed_cop: Number(form.price_reviewed_cop),
        price_auto_cop: Number(form.price_auto_cop),
        title: form.title, subtitle: form.subtitle || "", delivery_note: form.delivery_note || "",
        deliverables: form.deliverables.filter((d) => d.label.trim() && d.summary.trim()),
      };
      const { data } = await api.put("/admin/n2-offer", body);
      setForm(data);
      await loadN2Offer(true);
      toast.success("Oferta Kit Documental guardada");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-10" data-testid="n2-offer-settings">
      <h2 className="font-serif text-xl text-gray-900 mb-1">Oferta comercial Kit Documental (N2)</h2>
      <p className="text-xs text-gray-500 mb-3">Precio y entregables que ve el cliente en la tarjeta del diagnóstico. El cobro usa exactamente estos precios.</p>
      <div className="bg-white border border-gray-200 p-5 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-xs text-gray-600">Precio con revisión humana (COP)
            <Input type="number" value={form.price_reviewed_cop} onChange={(e) => set("price_reviewed_cop", e.target.value)} className="rounded-sm h-9 mt-1" data-testid="n2-offer-price-reviewed-input" />
            <span className="text-[11px] text-gray-400">{formatCOP(form.price_reviewed_cop)}</span>
          </label>
          <label className="text-xs text-gray-600">Precio automático sin revisión (COP)
            <Input type="number" value={form.price_auto_cop} onChange={(e) => set("price_auto_cop", e.target.value)} className="rounded-sm h-9 mt-1" data-testid="n2-offer-price-auto-input" />
            <span className="text-[11px] text-gray-400">{formatCOP(form.price_auto_cop)}</span>
          </label>
        </div>
        <label className="block text-xs text-gray-600">Título
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-sm h-9 mt-1" data-testid="n2-offer-title-input" />
        </label>
        <label className="block text-xs text-gray-600">Subtítulo
          <Input value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="rounded-sm h-9 mt-1" data-testid="n2-offer-subtitle-input" />
        </label>
        <div>
          <p className="text-xs text-gray-600 mb-1">Entregables</p>
          {form.deliverables.map((d, i) => (
            <div key={i} className="grid md:grid-cols-3 gap-2 mb-2" data-testid={`n2-offer-deliverable-row-${i}`}>
              <Input value={d.label} onChange={(e) => setDel(i, "label", e.target.value)} placeholder="Nombre" className="rounded-sm h-9" data-testid={`n2-offer-deliverable-label-${i}`} />
              <Input value={d.summary} onChange={(e) => setDel(i, "summary", e.target.value)} placeholder="Descripción" className="rounded-sm h-9 md:col-span-2" data-testid={`n2-offer-deliverable-summary-${i}`} />
            </div>
          ))}
          <Button type="button" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => set("deliverables", [...form.deliverables, { label: "", summary: "" }])} data-testid="n2-offer-add-deliverable">+ Añadir entregable</Button>
        </div>
        <label className="block text-xs text-gray-600">Nota de entrega
          <Input value={form.delivery_note || ""} onChange={(e) => set("delivery_note", e.target.value)} className="rounded-sm h-9 mt-1" data-testid="n2-offer-delivery-note-input" />
        </label>
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} className="rounded-sm h-9" data-testid="n2-offer-save">{saving ? "Guardando…" : "Guardar oferta"}</Button>
          {form.updated_at && <span className="text-[11px] text-gray-400">Actualizada {formatDate(form.updated_at)}</span>}
        </div>
      </div>
    </section>
  );
}
